import Resumable from 'resumablejs';
import { uploadStatus, UPLOADING_FILES } from '../../helpers/constants';

const { localStorage } = window;

const uuidv4 = require('uuid/v4');

const unfinished = JSON.parse(localStorage.getItem(UPLOADING_FILES));

const addUploadingItemToLocalStorage = (file, params) => {
  const unfinishedItems = JSON.parse(localStorage.getItem(UPLOADING_FILES));
  let result = unfinishedItems;
  if (!result) {
    result = {};
  }
  result[file.uniqueIdentifier] = {
    filename: file.fileName,
    uploadParams: params,
  };
  localStorage.setItem(UPLOADING_FILES, JSON.stringify(result));
};

const removeUploadingItemFromLocalStorage = (id) => {
  const unfinishedItems = JSON.parse(localStorage.getItem(UPLOADING_FILES));
  if (!unfinishedItems) return;
  delete unfinishedItems[id];
  localStorage.setItem(UPLOADING_FILES, JSON.stringify(unfinishedItems));
};

const {
  LOADING,
  ERROR,
  SUCCESS,
  PAUSE,
  ABORTED,
} = uploadStatus;

const initialState = {
  uploads: {},
  unfinishedUploads: unfinished,
};
const mutations = {
  setFileProgress(state, payload) {
    const fileInfo = {
      ...payload,
    };
    state.uploads[payload.id] = fileInfo;
    state.uploads = {
      ...state.uploads,
    };
  },
  stopUpload(state, id) {
    const upload = state.uploads[id];
    upload.resumable.cancel();
    upload.status = ABORTED;
    upload.progress = 100;
    state.uploads[id] = {
      ...upload,
    };
  },
  resumeUpload(state, id) {
    const upload = state.uploads[id];
    upload.resumable.upload();
    upload.status = LOADING;
    state.uploads[id] = {
      ...upload,
    };
  },
  pauseUpload(state, id) {
    const upload = state.uploads[id];
    upload.resumable.pause();
    upload.status = PAUSE;
    state.uploads[id] = {
      ...upload,
    };
  },
  retryUpload(state, id) {
    const upload = state.uploads[id];
    upload.resumable.removeFile(upload.resumableFile);
    upload.resumable.addFile(upload.file);
    [upload.resumableFile] = upload.resumable.files;
    const newId = upload.resumableFile.uniqueIdentifier;
    upload.id = newId;
    if (upload.status === ERROR) {
      upload.resumableFile.retry();
    } else {
      upload.resumable.upload();
    }
    upload.status = LOADING;
    delete state.uploads[id];
    removeUploadingItemFromLocalStorage(id);
  },
  removeUpload(state, id) {
    delete state.uploads[id];
    state.uploads = {
      ...state.uploads,
    };
  },
  removeUnfinishedUpload(state, id) {
    delete state.unfinishedUploads[id];
    state.unfinishedUploads = {
      ...state.unfinishedUploads,
    };
    removeUploadingItemFromLocalStorage(id);
  },
};
const actions = {
  startUpload({ commit }, {
    files,
    entityId,
    apiPrefix,
    apiHost,
    token,
    contentType,
    acceptedFiles,
    ...rest
  }) {
    const commitProgress = (resumableFile, status, fileProgress, resumable) => commit('setFileProgress', {
      id: resumableFile.uniqueIdentifier,
      entityId,
      file: resumableFile.file,
      status,
      fileProgress,
      resumable,
      resumableFile,
      contentType,
      ...rest,
    });
    files.forEach((fileObj) => {
      const query = {
        ...rest,
      };
      if (entityId) {
        query.entity_id = entityId;
      }
      if (contentType) {
        query.content_type = contentType;
      }
      const r = new Resumable({
        target: `${apiHost}${apiPrefix}files/chunk-upload`,
        chunkSize: 5 * 1024 * 1024, // 5MB
        simultaneousUploads: 3,
        testChunks: true,
        maxChunkRetries: 5,
        fileParameterName: 'file',
        headers: {
          authorization: `${token.token_type} ${token.access_token}`,
          Accept: 'application/json',
        },
        generateUniqueIdentifier: uuidv4,
        query,
      });

      r.addFile(fileObj);
      r.on('fileAdded', (file) => {
        commitProgress(file, LOADING, 0, r);
        r.upload();
        addUploadingItemToLocalStorage(file, {
          id: file.uniqueIdentifier,
          entityId,
          apiPrefix,
          apiHost,
          token,
          contentType,
          acceptedFiles,
          ...rest,
        });
      });
      r.on('fileProgress', (file) => {
        commitProgress(file, LOADING, Math.round(file.progress() * 10000) / 100, r);
      });
      r.on('fileSuccess', (file, json) => {
        if (rest.is_public_file) {
          const urls = JSON.parse(json);
          rest.urls = [ ...urls.urls ];
        }
        // const uploadedFile = JSON.parse(json);
        // file.file.thumbnail = uploadedFile.thumbnail;
        commitProgress(file, SUCCESS, Math.round(file.progress() * 10000) / 100, r);
        removeUploadingItemFromLocalStorage(file.uniqueIdentifier);
      });
      r.on('fileError', (file) => {
        commitProgress(file, ERROR, Math.round(file.progress() * 10000) / 100, r);
        removeUploadingItemFromLocalStorage(file.uniqueIdentifier);
      });
    });
  },

  stopUpload({ commit }, id) {
    commit('stopUpload', id);
  },
  resumeUpload({ commit }, id) {
    commit('resumeUpload', id);
  },
  pauseUpload({ commit }, id) {
    commit('pauseUpload', id);
  },
  retryUpload({ commit }, id) {
    commit('retryUpload', id);
  },
  removeUpload({ commit }, id) {
    commit('removeUpload', id);
  },

  restoreUpload({ dispatch }, payload) {
    dispatch('startUpload', payload);
  },
  removeUnfinishedUpload({ commit }, id) {
    commit('removeUnfinishedUpload', id);
  },
};
const getters = {
  uploads(state) {
    const { uploads } = state;
    const uploadsArray = [];
    Object.values(uploads).forEach((upload) => {
      uploadsArray.push({
        id: upload.id,
        fileName: upload.file.name,
        fileProgress: upload.fileProgress,
        status: upload.status,
      });
    });
    return uploadsArray;
  },
  unfinishedUploads(state) {
    const { unfinishedUploads } = state;
    const result = [];
    if (!unfinishedUploads) {
      return result;
    }
    Object.keys(unfinishedUploads).forEach((id) => {
      result.push({
        id,
        ...unfinishedUploads[id],
      });
    });
    return result;
  },

  getUploadByEntityId: state => (entityId, contentType, fileType) => {
    let uploadingFile;
    Object.values(state.uploads).forEach((item) => {
      if (uploadingFile) {
        return;
      }
      if (+item.entityId === +entityId && item.contentType === contentType
        && fileType === item.file_type) {
        uploadingFile = item;
      }
    });
    if (!uploadingFile) {
      return {};
    }
    return {
      id: uploadingFile.id,
      status: uploadingFile.status,
      file: uploadingFile.file,
    };
  },
  getUploadsList: state => (entityId, contentType, fileType) => {
    const uploadingList = [];
    Object.values(state.uploads).forEach((item) => {
      if (+item.entityId === +entityId && item.contentType === contentType
        && fileType === item.file_type) {
        uploadingList.push(item);
      }
    });
    return uploadingList;
  },
  generalProgress: (state) => {
    const uploads = Object.values(state.uploads).filter(upload => upload.status !== ABORTED);
    const progressSum = uploads.reduce((progress, item) => progress + item.fileProgress, 0);
    return Math.round((progressSum / uploads.length) * 100) / 100;
  },

  publicFilesUploads: state => Object.values(state.uploads).filter(item => item.is_public_file),

  generalStatus: (state) => {
    const uploads = Object.values(state.uploads);
    let isLoading = false;
    let isPaused = false;
    let isSuccess = true;
    let hasError = false;
    for (let i = 0; i < uploads.length; i++) {
      const upload = uploads[i];
      switch (upload.status) {
      case LOADING: {
        isLoading = true;
        isSuccess = false;
        break;
      }
      case ERROR: {
        hasError = true;
        isSuccess = false;
        break;
      }
      case PAUSE: {
        isPaused = true;
        isSuccess = false;
        break;
      }
      case SUCCESS: {
        break;
      }
      default: {
        isSuccess = false;
        break;
      }
      }
    }
    if (hasError) {
      return ERROR;
    }
    if (isPaused) {
      return PAUSE;
    }
    if (isLoading) {
      return LOADING;
    }
    if (isSuccess) {
      return SUCCESS;
    }
    return ABORTED;
  },
  hasUploadingFiles: (state) => {
    const uploads = Object.values(state.uploads);
    let isThereUploadingFile = false;
    for (let i = 0; i < uploads.length; i++) {
      const upload = uploads[i];
      if (upload.status === LOADING) {
        isThereUploadingFile = true;
        break;
      }
    }
    return isThereUploadingFile;
  },
};

export default {
  namespaced: true,
  mutations,
  actions,
  getters,
  state: initialState,
};



// WEBPACK FOOTER //
// ./src/store/modules/uploadManager.js